import { CredentialController } from '@/components/forms/FormController'
import ErrorMessage from '@/components/utils/ErrorMessage'
import { AuthResult, SignInStep, UserSignInInput } from '@/context/AuthContext'
import { zodResolver } from '@/lib/zod'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@droidmap/frontend-ui'

interface SignInFormProps {
  onSignIn: (userDetails: UserSignInInput) => Promise<AuthResult>
}

const signInSchema = z.object({
  username: z.string().min(1, 'Username or email address is required'),
  password: z.string().min(1, 'Password is required'),
})

type SignInFormValues = z.infer<typeof signInSchema>

const initialValues: SignInFormValues = {
  username: '',
  password: '',
}

export default function SignInForm({ onSignIn }: SignInFormProps) {
  const navigate = useNavigate()
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  })

  const onSubmit = async (data: SignInFormValues) => {
    setError('')
    setLoading(true)

    try {
      const result = await onSignIn(data)
      if (result.success) {
        // Success is handled by parent component (router invalidation)
      } else if (result.nextStep === SignInStep.CONFIRM_SIGN_UP) {
        // An unconfirmed user who typed their email is only resolvable by the
        // username the sign-in looked up for us — prefill that, so the confirm
        // screen doesn't have to repeat the lookup. See #2407.
        navigate({
          to: '/confirm-signup',
          search: { username: result.resolvedUsername ?? data.username },
        })
      } else {
        setError(result.message)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <CredentialController
        name="username"
        control={control}
        label="Username / Email address"
        errors={errors}
        autoComplete="username"
      />
      <CredentialController
        name="password"
        control={control}
        label="Password"
        errors={errors}
        type="password"
        autoComplete="current-password"
      />
      <div className="text-sm">
        <button
          type="button"
          onClick={() => navigate({ to: '/forgot-password' })}
          className="font-semibold text-accent hover:text-accent-hi"
        >
          Forgot password?
        </button>
      </div>
      {error && <ErrorMessage content={error} />}
      <Button type="submit" className="w-full" isLoading={loading}>
        Sign in
      </Button>
    </form>
  )
}
